import { getDistance, getAngularDiff, rad2deg, deg2rad, getAzimuth } from "./utils.js";
import CursorImg from "../img/click2go.png";
import CursorFailImg from "../img/click2notgo.png";
import { DISABLED_LEVEL } from "./indoor.js";

const CURSOR_SIZE = 125;
const MAX_PROJ_DIST = 50;
const LOWEST_PITCH_DEG = -45;
const DESTINATION_MARGIN = 0.0001;

/**
 * Click and go handles the "click anywhere in picture and go somewhere" mechanism.
 * It selects best picture nearby based on quality, distance, date and so on.
 * 
 * @class Panoramax.utils.ClickAndGo
 * @typicalname clickAndGo
 * @param {Photo} psv The Photo Sphere Viewer component to plug onto.
 * @param {boolean} [isDesktop=true] Adapt interaction based on desktop/mobile environment
 */
export default class ClickAndGo {
	constructor(psv, isDesktop = true) {
		this.psv = psv;
		this.isDesktop = isDesktop;

		// Create cursor container
		if(this.isDesktop) {
			this._cursor = document.createElement("img");
			this._cursor.src = CursorImg;
			this.psv.container.appendChild(this._cursor);
			this._cursor.style.width = `${CURSOR_SIZE}px`;
			this._cursor.style.height = `${CURSOR_SIZE}px`;
			this._cursor.style.position = "absolute";
			this._cursor.style.zIndex = 10;
			this._cursor.style.pointerEvents = "none";
			this._cursor.style.display = "none";
			this._hideCursor = false;
		}
		this._couldGoSomewhere = false;
		this._mapPreviewPic = null;

		// Handle all kind of events
		this.psv.container.addEventListener("mousemove", this._onPointerMove.bind(this));
		this.psv.container.addEventListener("mouseleave", this._onPointerLeave.bind(this));
		this.psv._myMarkers.addEventListener("enter-marker", this._onHoverOtherStuff.bind(this));
		this.psv._myMarkers.addEventListener("leave-marker", this._onLeaveOtherStuff.bind(this));
		this.psv._myVTour.addEventListener("enter-arrow", this._onHoverOtherStuff.bind(this));
		this.psv._myVTour.addEventListener("leave-arrow", this._onLeaveOtherStuff.bind(this));
		this.psv.addEventListener("sequence-playing", this._onHoverOtherStuff.bind(this));
		this.psv.addEventListener("sequence-stopped", this._onLeaveOtherStuff.bind(this));
		this.psv.addEventListener("sequence-playing", this._onHoverOtherStuff.bind(this));
		this.psv.addEventListener("pictures-navigation-changed", this._onPSVPicNavChanged.bind(this));
		this.psv.addEventListener("click", this._onClick.bind(this));
	}

	/**
	 * Handle moving viewer to desired picture nearby.
	 * @param {Event} e The click event from PSV
	 * @private
	 */
	async _onClick(e) {
		if(
			this.psv._sequencePlaying
			|| this.psv.getPicturesNavigation() === "pic"
			|| this.psv._myVTour.state.loadingNode
			|| this.psv.getSelectedAnnotations().length > 0
			|| (this.psv._parent.map && this.psv._parent.isMapWide?.())
		) { return; }
		
		e.stopPropagation();

		// If a preview marker is shown, go to it
		if(this._mapPreviewPic) {
			this.psv._keepSameAngle = true;
			this.psv._parent.select(this._mapPreviewPic.collection, this._mapPreviewPic.id);
			this._mapPreviewPic = null;
			return;
		}

		const meta = this.psv.getPictureMetadata();
		if(!meta) { return; }

		// No preview, try to find best spot to go to
		let projectedDistance = Math.min(this._pitchDegToProjectedDistance(rad2deg(e.data.pitch), hasGoodYPR(meta)), MAX_PROJ_DIST);
		if(projectedDistance > 0) {
			this._couldGoSomewhere = true;

			const headingDeg = (rad2deg(e.data.yaw) + this.psv.getPictureOriginalHeading()) % 360;
			const destCoords = projectCoordsBearingDist(meta.gps, projectedDistance, headingDeg);
			let nearbyPics;
			
			// Check if map is crowded or not to reduce max distance
			// Also use map for finding candidate pictures
			if(this.psv._parent.map) {
				nearbyPics = this._getMapNearbyPictures(destCoords);
			}

			// No map? Use API to find candidates pictures
			if(!nearbyPics || nearbyPics?.length === 0) {
				nearbyPics = (await this.psv._parent.api.getPicturesAroundCoordinates(destCoords[1], destCoords[0], DESTINATION_MARGIN, 50))?.features;
			}

			// Select best match
			const bestPic = this._bestPicAround(meta, destCoords, headingDeg, nearbyPics);
			if(bestPic) {
				this.psv._keepSameAngle = true;
				this.psv._parent.select(bestPic.collection, bestPic.id);
			} else {
				if(this.isDesktop) {
					this._cursor.src = CursorFailImg;
					setTimeout(() => this._cursor.src = CursorImg, 1500);
				}
			}
		} else {
			this._couldGoSomewhere = false;
		}
	}

	/**
	 * Handle moving preview cursor.
	 * @param {Event} e The move event from PSV
	 * @private
	 */
	_onPointerMove(e) {
		this._couldGoSomewhere = false;
		const yp = this.psv.getPositionFromEvent(e);
		if(!yp || this.psv.getPicturesNavigation() === "pic" || this.psv.getSelectedAnnotations().length > 0) { return; }

		const map = this.psv._parent?.map;
		const meta = this.psv.getPictureMetadata();
		if(!meta) { return; }

		let projectedDistance = Math.min(this._pitchDegToProjectedDistance(rad2deg(yp.pitch), hasGoodYPR(meta)), MAX_PROJ_DIST);
		if(!this._hideCursor && projectedDistance > 0) {
			this._couldGoSomewhere = true;

			if(this.isDesktop) {
				const sizeFactor = cursorSizeFactor(rad2deg(yp.pitch));
				const cursorHeight = CURSOR_SIZE * 2/5 * sizeFactor;
				const cursorWidth = CURSOR_SIZE * sizeFactor;
				this._cursor.style.top = `${e.layerY - 3 * cursorHeight / 5}px`;
				this._cursor.style.left = `${e.layerX - cursorWidth / 2}px`;
				this._cursor.style.height = `${cursorHeight}px`;
				this._cursor.style.width = `${cursorWidth}px`;
				this._cursor.style.display = "block";

				// Try to preview on map as well
				clearTimeout(this._throttledBestPicMap);
				this._throttledBestPicMap = setTimeout(() => {
					const headingDeg = (rad2deg(yp.yaw) + this.psv.getPictureOriginalHeading()) % 360;
					const destCoords = projectCoordsBearingDist(meta.gps, projectedDistance, headingDeg);
					const nearbyPics = this._getMapNearbyPictures(destCoords);

					if(nearbyPics?.length > 0) {
						const headingDeg = (rad2deg(yp.yaw) + this.psv.getPictureOriginalHeading()) % 360;
						const destCoords = projectCoordsBearingDist(meta.gps, projectedDistance, headingDeg);
						const bestPic = this._bestPicAround(meta, destCoords, headingDeg, nearbyPics);
						if(!bestPic) {
							this._cursor.src = CursorFailImg;
							map._picMarkerPreview?.remove();
							this._mapPreviewPic = null;
						} else {
							this._cursor.src = CursorImg;
							this._mapPreviewPic = bestPic;
							map._picMarkerPreview
								.setLngLat(bestPic.geometry.coordinates)
								.setRotation(
									bestPic.properties?.["pers:interior_orientation"]?.field_of_view === 360 ?
									(this.psv.getXYZ().x || 0)
									: (bestPic.properties?.["view:azimuth"] || 0)
								)
								.addTo(map);
						}
					} else {
						this._cursor.src = CursorImg;
						this._mapPreviewPic = null;

						if(map && (this._isMapShowingPictures() || this._getMapNearbySequences(destCoords).length === 0)) {
							this._cursor.src = CursorFailImg;
							map?._picMarkerPreview?.remove();
							
						} else {
							this._cursor.src = CursorImg;
							map?._picMarkerPreview
								.setLngLat(destCoords)
								.setRotation(headingDeg)
								.addTo(map);
						}
					}
				}, 10);
			}
		} else if(this.isDesktop) {
			this._cursor.style.display = "none";
			this._mapPreviewPic = null;

			// To not interfere with PSV native arrows
			if(!this._hideCursor) {
				map?._picMarkerPreview?.remove();
			}
		}
	}

	/**
	 * Handle hiding cursor.
	 * @private
	 */
	_onPointerLeave() {
		if(this.isDesktop) { this._cursor.style.display = "none"; }
		this._couldGoSomewhere = false;
	}

	/**
	 * Handle hiding cursor if user hovers something else in PSV.
	 * @private
	 */
	_onHoverOtherStuff() {
		this._hideCursor = true;
		this._onPointerLeave();
	}

	/**
	 * Handle showing back cursor if user doesn't hover anymore something else in PSV.
	 * @private
	 */
	_onLeaveOtherStuff() {
		this._hideCursor = false;
	}

	/**
	 * Handle pictures navigation filter changes.
	 * @private
	 */
	_onPSVPicNavChanged(e) {
		switch(e?.detail?.value) {
			case "pic":
				this._onHoverOtherStuff();
				break;
			case "seq":
			case "any":
			default:
				this._onLeaveOtherStuff();
		}
	}

	/**
	 * Transform a vertical / pitch value into projection distance in meters.
	 * This allows to estimate where user want to go depending on how near to horizon it clicks.
	 * @param {number} pitchDeg The vertical degrees value (-90 - 90°)
	 * @param {boolean} fromGoodYPR Is the currently shown image having an accurate Yaw/Pitch/Roll display?
	 * @returns {number} The projection distance, in meters.
	 * @private
	 */
	_pitchDegToProjectedDistance(pitchDeg, fromGoodYPR) {
		if(!fromGoodYPR) {
			return MAX_PROJ_DIST / 3;
		}
		if(!this.isDesktop) {
			if      (pitchDeg >= 20)  { return 0; }
			else if (pitchDeg >= 4)   { return MAX_PROJ_DIST / 3; }
			else if (pitchDeg >= -4)  { return MAX_PROJ_DIST / 1.5; }
			else if (pitchDeg >= -20) { return MAX_PROJ_DIST / 3; }
			else                      { return 0; }
		} else {
			if      (pitchDeg >= 30)               { return 0; }
			else if (pitchDeg >= -2.5)             { return MAX_PROJ_DIST; }
			else if (pitchDeg >= -6)               { return MAX_PROJ_DIST / 2; }
			else if (pitchDeg >= -15)              { return MAX_PROJ_DIST / 4; }
			else if (pitchDeg >= LOWEST_PITCH_DEG) { return MAX_PROJ_DIST / 8; }
			else                                   { return 0; }
		}
	}

	/**
	 * Check if map has pictures vector data loaded
	 * @private
	 */
	_isMapShowingPictures() {
		if(this.psv._parent.map) {
			const map = this.psv._parent.map;
			let layer = map.getLayersOrder().find(lid => lid.endsWith("pictures"));
			if(layer) {
				const features = map.queryRenderedFeatures({layers: [layer]});
				return features.length > 0;
			}
		}
		return false;
	}

	/**
	 * Find nearby candidates pictures by using map data (if available)
	 * @private
	 */
	_getMapNearbyPictures(destCoords) {
		const map = this.psv._parent.map;
		if(map) {
			const msc = this.psv._parent?.mapStyleComposer;
			if(msc) {
				const src = Object.keys(msc.layerRanges.panoramax[msc.panoramax].sources)[0];
				if(src) {
					return map
						.querySourceFeatures(src, {
							sourceLayer: "pictures",
							filter: ["within", { type: "Polygon", coordinates: [[
								[destCoords[0] - DESTINATION_MARGIN, destCoords[1] - DESTINATION_MARGIN],
								[destCoords[0] + DESTINATION_MARGIN, destCoords[1] - DESTINATION_MARGIN],
								[destCoords[0] + DESTINATION_MARGIN, destCoords[1] + DESTINATION_MARGIN],
								[destCoords[0] - DESTINATION_MARGIN, destCoords[1] + DESTINATION_MARGIN],
								[destCoords[0] - DESTINATION_MARGIN, destCoords[1] - DESTINATION_MARGIN]
							]]}]
						})
						.map(f => ({
							id: f.properties.id,
							collection: f.properties.first_sequence || f.properties?.sequences?.[0],
							geometry: f.geometry,
							properties: {
								datetime: f.properties.datetime || f.properties.ts,
								"view:azimuth": f.properties?.["view:azimuth"] || f.properties.heading,
								"pers:interior_orientation": f.properties?.["pers:interior_orientation"] || {
									field_of_view: f.properties.type === "equirectangular" ? 360 : undefined
								}
							}
						}));
				}
			}
		}
		return null;
	}

	/**
	 * Find nearby sequences by using map data (if available)
	 * @private
	 */
	_getMapNearbySequences(destCoords) {
		const map = this.psv._parent.map;
		if(map) {
			const layer = map.getLayersOrder().find(lid => lid.endsWith("sequences"));
			if(layer) {
				const margin = DESTINATION_MARGIN / 4;
				const bbox = [
					map.project([destCoords[0] - margin, destCoords[1] - margin]),
					map.project([destCoords[0] + margin, destCoords[1] + margin])
				];
				return map.queryRenderedFeatures(bbox, {layers: [layer]});
			}
		}
		return [];
	}

	/**
	 * Find best picture nearby according to user click in PSV
	 * @param {object} meta The currently selected picture metadata
	 * @param {number[]} destCoords The projected point coordinates as [x,y]
	 * @param {number} headingDeg The heading of destination compared to current picture, in degrees (0-360°)
	 * @param {object[]} nearbyPics List of API surrounding pictures
	 * @returns {object|null} Matching picture, or null if no satisfying picture found
	 * @private
	 */
	_bestPicAround(meta, destCoords, headingDeg, nearbyPics) {
		if (!Array.isArray(nearbyPics) || !nearbyPics.length) { return null; }
		const curDate = new Date(meta.properties?.datetime);

		// Build candidate list with all relevant attributes
		// We prefer same date, 360° imagery, nearby wanted destination
		// And fallback progressively to different date, not 360°, far appart wanted destination
		const candidates = nearbyPics
			.filter(p => p.id !== meta.id) // Filter out selected picture
			.map(p => {
				const is360 = p.properties?.["pers:interior_orientation"]?.field_of_view === 360;
				const dist = getDistance(destCoords, p.geometry.coordinates);
				const sequenceCheck = p.collection === meta.sequence?.id || this.psv.getPicturesNavigation() === "any";

				// Heading
				const viewAz = p.properties?.["view:azimuth"];
				const headingMatch = is360 || (typeof viewAz === "number" && getAngularDiff(headingDeg, viewAz) <= 45);
				const inRightDir = Math.abs((headingDeg - getAzimuth(meta.gps, p.geometry.coordinates)) % 360) <= 45;

				// Date
				const picDate = new Date(p.properties?.datetime);
				const sameDay = curDate.toDateString() === picDate.toDateString();
				const daysDiff = Math.abs((curDate - picDate) / (1000 * 60 * 60 * 24));

				// Indoor level
				let sameLevel = true;
				if(this.psv._parent.map) {
					const map = this.psv._parent.map;
					if(map.indoorEqual && map.indoorEqual.level !== DISABLED_LEVEL) {
						sameLevel = map._indoorFeaturesIDs.pictures[map.indoorEqual.level]?.has(p.id);

						// Ground level: check if pic is not on another level
						if(!sameLevel && map.indoorEqual.level === "0") {
							sameLevel = true;
							Object.entries(map._indoorFeaturesIDs.pictures).forEach(([lvl, pics]) => {
								if(sameLevel && lvl !== "0") {
									sameLevel = !pics.has(p.id);
								}
							});
						}
					}
				}

				// Matching category (lower is better)
				let category = Infinity;
				if(sequenceCheck && inRightDir && sameLevel) {
					if(sameDay && is360) { category = 1; }
					else if(is360 && daysDiff <= 30) { category = 2; }
					else if(headingMatch && daysDiff <= 30) { category = 3; }
					else if(is360) { category = 4; }
					else if(headingMatch) { category = 5; }
				}

				return { p, category, dist, daysDiff, headingMatch, sameDay, is360 };
			});

		if (!candidates.length) { return null; }

		// Sort by category first, then by distance, then by date proximity
		candidates.sort((a, b) => {
			if (a.category !== b.category) { return a.category - b.category; }
			if (a.dist !== b.dist) { return a.dist - b.dist; }
			if (a.daysDiff !== b.daysDiff) { return a.daysDiff - b.daysDiff; }
			return 0;
		});

		const best = candidates[0];
		return best.category < Infinity ? best.p : null;
	}
}

/**
 * Is the current image having Yaw/Pitch/Roll correction?
 * @param {object} metadata Current picture metadata
 * @returns {boolean} True if image has pitch/roll correction
 */
function hasGoodYPR(metadata) {
	return (
		metadata.properties?.["pers:interior_orientation"]?.field_of_view === 360
		|| metadata?.properties?.["pers:roll"] !== undefined
		|| metadata?.properties?.["pers:pitch"] !== undefined
	);
}

/**
 * Project coordinates into a certain direction, at a certain distance.
 * @param {number[]} coords [x, y] coordinates
 * @param {number} dist How far to project, in meters
 * @param {number} bearing The heading in degrees (0-360°)
 * @returns {number[]} [x,y] coordinates of projected point
 * @private
 */
function projectCoordsBearingDist(coords, dist, bearing) {
	const [lon, lat] = coords;
	const R = 6378137;
	const phi1 = deg2rad(lat);
	const d1 = deg2rad(lon);
	const th  = deg2rad(bearing);
	const dlt = dist / R;
	const sinphi1 = Math.sin(phi1);
	const cosphi1 = Math.cos(phi1);
	const sindlt  = Math.sin(dlt);
	const cosdlt  = Math.cos(dlt);
	const sinth  = Math.sin(th);
	const costh  = Math.cos(th);
	const sinphi2 = sinphi1 * cosdlt + cosphi1 * sindlt * costh;
	const phi2    = Math.asin(sinphi2);
	const y = sinth * sindlt * cosphi1;
	const x = cosdlt - sinphi1 * sinphi2;
	const d2 = d1 + Math.atan2(y, x);
	return [rad2deg(d2), rad2deg(phi2)];
}

/**
 * Transforms PSV pitch into a size factor for cursor display.
 * @param {number} pitch The PSV vertical pitch, in degrees
 * @returns {number} The size factor, 0-1
 * @private
 */
function cursorSizeFactor(pitch) {
	pitch = Math.max(Math.min(pitch, 0), LOWEST_PITCH_DEG);
	const v = (pitch - LOWEST_PITCH_DEG) / (-LOWEST_PITCH_DEG);
	const sigma = 0.2;
	return Math.max(1 - Math.exp(-Math.pow(v - 1, 2) / (2 * sigma * sigma)), 0.4);
}
